import React from 'react';
import { Alert, Button, Card, Divider, Space, Spin, Tag, Typography } from 'antd';
import {
  CloseCircleOutlined,
  CommentOutlined,
  DeleteOutlined,
  FlagOutlined,
  PlayCircleOutlined,
  StopOutlined,
  UnlockOutlined,
  UserDeleteOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { colors } from '../../../theme';
import './ReportDetailContent.css';

const { Title, Text } = Typography;

function formatUserFullName(user) {
  if (!user || typeof user !== 'object') return null;
  const fullName = [user.firstName, user.lastName, user.secondLastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  return fullName || null;
}

function formatPersonName(user) {
  if (!user || typeof user !== 'object') return '-';
  return formatUserFullName(user) || user.nickname || user.email || '-';
}

function formatContentType(type) {
  if (type === 'VIDEO') return 'Video';
  if (type === 'COMMENT') return 'Comentario';
  return type || '-';
}

function getInitials(user) {
  const fullName = formatUserFullName(user);
  if (fullName) {
    const parts = fullName.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return fullName.slice(0, 2).toUpperCase();
  }
  if (user?.nickname) return String(user.nickname).slice(0, 2).toUpperCase();
  if (user?.email) return String(user.email).slice(0, 2).toUpperCase();
  return '?';
}

function formatCompetitionLabel(competition) {
  if (competition == null || competition === '') return '-';
  if (typeof competition === 'string' || typeof competition === 'number') {
    return String(competition);
  }
  return competition.description || competition.name || '-';
}

const REPORT_STATUS_MAP = {
  PENDING: { label: 'Pendiente', color: 'warning' },
  REVIEWING: { label: 'En revisión', color: 'processing' },
  RESOLVED: { label: 'Resuelto', color: 'success' },
  DISMISSED: { label: 'Desestimado', color: 'default' },
};

const MODERATION_OPEN_STATUSES = ['PENDING', 'REVIEWING'];

function reportStatusTag(status) {
  const mapped = REPORT_STATUS_MAP[status] ?? {
    label: status || '-',
    color: 'default',
  };
  return <Tag color={mapped.color}>{mapped.label}</Tag>;
}

function MetaItem({ label, children }) {
  return (
    <div className="report-detail-meta-item">
      <span className="report-detail-meta-label">{label}</span>
      <div className="report-detail-meta-value">{children}</div>
    </div>
  );
}

function ActionCard({
  icon,
  iconClass,
  title,
  titleClass,
  description,
  disabled,
  onClick,
}) {
  return (
    <Card
      className={`report-action-card ${disabled ? 'is-disabled' : 'is-clickable'}`}
      hoverable={!disabled}
      onClick={() => {
        if (!disabled) onClick();
      }}
      role="button"
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <div className="report-action-card__inner">
        <span className={`report-action-card__icon ${iconClass}`}>{icon}</span>
        <div>
          <span className={`report-action-card__title ${titleClass}`}>{title}</span>
          <span className="report-action-card__desc">{description}</span>
        </div>
      </div>
    </Card>
  );
}

function CommentReportDetail({
  detail,
  canModerate,
  applyingModerationAction,
  onPreviewVideo,
  onModerationAction,
}) {
  const video = detail?.video;
  const report = detail?.report;
  const comment = detail?.comment;
  const reportedBy = detail?.reportedBy ?? report?.reportedBy;
  const reason = detail?.reason ?? report?.reason;
  const commentOwner = detail?.commentOwner;
  const canPlay = Boolean(video?.urlVideo);
  const isVideoDeleted = video?.deletedAt != null;
  const isCommentDeleted = comment?.deletedAt != null;
  const isReply = comment?.parentId != null;
  const canAct =
    canModerate && MODERATION_OPEN_STATUSES.includes(report?.status);
  const isAttended =
    report?.status === 'RESOLVED' || report?.status === 'DISMISSED';

  const openPreview = () => {
    if (!canPlay) return;
    onPreviewVideo({
      urlVideo: video.urlVideo,
      title: video.title || 'Vista previa',
    });
  };

  return (
    <div className="report-detail">
      <Alert
        type="error"
        showIcon
        icon={<FlagOutlined />}
        message="Este comentario ha sido reportado por los usuarios."
      />
      {isCommentDeleted ? (
        <Alert
          type="warning"
          showIcon
          message="Este comentario ya no está disponible para los usuarios."
        />
      ) : null}
      {isVideoDeleted ? (
        <Alert
          type="warning"
          showIcon
          message="Este video ya no está disponible para los usuarios."
        />
      ) : null}

      <div className="report-detail-split">
        <div className="report-detail-split__left">
          <div className="report-detail-video">
            <div
              className={`report-detail-thumb${canPlay ? ' is-playable' : ''}`}
              role={canPlay ? 'button' : undefined}
              tabIndex={canPlay ? 0 : undefined}
              aria-label={canPlay ? 'Reproducir vista previa del video' : undefined}
              onClick={openPreview}
              onKeyDown={(e) => {
                if (!canPlay) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openPreview();
                }
              }}
            >
              {video?.urlThumbnail ? (
                <img src={video.urlThumbnail} alt="" />
              ) : (
                <div className="report-detail-thumb__placeholder">
                  <VideoCameraOutlined style={{ color: '#fff', fontSize: 24 }} />
                </div>
              )}
              {canPlay && (
                <div className="report-detail-thumb__play" aria-hidden>
                  <PlayCircleOutlined
                    style={{
                      fontSize: 32,
                      color: '#fff',
                      filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.6))',
                    }}
                  />
                </div>
              )}
            </div>

            <div className="report-detail-video-meta">
              <p className="report-detail-video-title">{video?.title || '-'}</p>
              <p className="report-detail-video-line">
                Competencia: {formatCompetitionLabel(video?.competition)}
              </p>
              <p className="report-detail-video-line">
                Jugador: {video?.playerName || '-'}
              </p>
              <p className="report-detail-video-line">
                Subido por: {formatPersonName(video?.uploadedBy)}
              </p>
              <p className="report-detail-video-line">
                Fecha de carga:{' '}
                {video?.createdAt
                  ? dayjs(video.createdAt).format('DD/MM/YYYY HH:mm')
                  : '-'}
              </p>
            </div>
          </div>
        </div>

        <div className="report-detail-split__right">
          <MetaItem label="Reportado por:">
            {formatPersonName(reportedBy)}
          </MetaItem>
          <MetaItem label="Fecha del reporte:">
            {report?.createdAt
              ? dayjs(report.createdAt).format('DD/MM/YYYY HH:mm')
              : '-'}
          </MetaItem>
          <MetaItem label="Motivo del reporte:">
            <Space direction="vertical" size={4}>
              <Tag color="error" style={{ marginInlineEnd: 0 }}>
                {reason?.name || '-'}
              </Tag>
              {reason?.description ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {reason.description}
                </Text>
              ) : null}
            </Space>
          </MetaItem>
          <MetaItem label="Tipo de contenido:">
            <span className="report-detail-content-type">
              <CommentOutlined style={{ color: colors.primary.main }} />
              {formatContentType('COMMENT')}
            </span>
          </MetaItem>
          <MetaItem label="Estado:">{reportStatusTag(report?.status)}</MetaItem>
        </div>
      </div>

      <div>
        <Title level={5} style={{ marginTop: 0, marginBottom: 12 }}>
          Comentario reportado
        </Title>
        <div className="report-detail-comment-card">
          <div className="report-detail-comment-avatar" aria-hidden>
            {getInitials(commentOwner)}
          </div>
          <div className="report-detail-comment-body">
            <div className="report-detail-comment-name">
              {formatPersonName(commentOwner)}
            </div>
            {isReply ? (
              <Tag style={{ width: 'fit-content', marginBottom: 8 }}>
                Respuesta a comentario
              </Tag>
            ) : null}
            <p className="report-detail-comment-text">
              {comment?.text || '-'}
            </p>
            <p className="report-detail-comment-date">
              {comment?.createdAt
                ? dayjs(comment.createdAt).format('DD/MM/YYYY HH:mm')
                : '-'}
            </p>
          </div>
        </div>
      </div>

      {canAct ? (
        <>
          <Divider style={{ margin: '4px 0 0' }} />
          <div>
            <Title level={5} style={{ marginTop: 8, marginBottom: 16 }}>
              Acciones disponibles
            </Title>
            <Spin spinning={applyingModerationAction}>
              <div className="report-actions-grid">
                <ActionCard
                  icon={<DeleteOutlined />}
                  iconClass="report-action-card__icon--danger"
                  title="Eliminar comentario"
                  titleClass="report-action-card__title--danger"
                  description="El comentario será eliminado de la plataforma."
                  disabled={applyingModerationAction}
                  onClick={() => onModerationAction('DELETE_COMMENT')}
                />
                <ActionCard
                  icon={<StopOutlined />}
                  iconClass="report-action-card__icon--warning"
                  title="Bloquear usuario"
                  titleClass="report-action-card__title--warning"
                  description="El usuario no podrá ingresar nuevamente a la aplicación."
                  disabled={applyingModerationAction}
                  onClick={() => onModerationAction('BLOCK_USER')}
                />
                <ActionCard
                  icon={<UserDeleteOutlined />}
                  iconClass="report-action-card__icon--combined"
                  title="Eliminar comentario y bloquear usuario"
                  titleClass="report-action-card__title--combined"
                  description="Se eliminará el comentario y se bloqueará al usuario."
                  disabled={applyingModerationAction}
                  onClick={() =>
                    onModerationAction('DELETE_COMMENT_AND_BLOCK_USER')
                  }
                />
                <ActionCard
                  icon={<CloseCircleOutlined />}
                  iconClass="report-action-card__icon--neutral"
                  title="No aplicar acción"
                  titleClass="report-action-card__title--neutral"
                  description="El comentario permanecerá disponible en la plataforma."
                  disabled={applyingModerationAction}
                  onClick={() => onModerationAction('NO_ACTION')}
                />
              </div>
            </Spin>
          </div>
        </>
      ) : isAttended ? (
        <Alert
          type="info"
          showIcon
          message="Este reporte ya fue atendido y no permite aplicar nuevas acciones."
        />
      ) : null}
    </div>
  );
}

const ReportDetailContent = ({
  detail,
  canModerate,
  applyingModerationAction,
  unbanningUser,
  onPreviewVideo,
  onModerationAction,
  onUnbanUser,
}) => {
  if (detail?.contentType === 'COMMENT') {
    return (
      <CommentReportDetail
        detail={detail}
        canModerate={canModerate}
        applyingModerationAction={applyingModerationAction}
        onPreviewVideo={onPreviewVideo}
        onModerationAction={onModerationAction}
      />
    );
  }

  const video = detail?.video;
  const report = detail?.report;
  const canPlay = Boolean(video?.urlVideo);
  const isDeleted = video?.deletedAt != null;
  const canAct =
    canModerate && MODERATION_OPEN_STATUSES.includes(report?.status);
  const showUnban =
    canModerate && video?.uploadedBy?.isBanned === true;

  const openPreview = () => {
    if (!canPlay) return;
    onPreviewVideo({
      urlVideo: video.urlVideo,
      title: video.title || 'Vista previa',
    });
  };

  return (
    <div className="report-detail">
      {isDeleted ? (
        <Alert
          type="warning"
          showIcon
          message="Este video fue eliminado."
        />
      ) : (
        <Alert
          type="error"
          showIcon
          icon={<FlagOutlined />}
          message="Este video ha sido reportado por los usuarios."
        />
      )}

      <div className="report-detail-split">
        <div className="report-detail-split__left">
          <div className="report-detail-video">
            <div
              className={`report-detail-thumb${canPlay ? ' is-playable' : ''}`}
              role={canPlay ? 'button' : undefined}
              tabIndex={canPlay ? 0 : undefined}
              aria-label={canPlay ? 'Reproducir vista previa del video' : undefined}
              onClick={openPreview}
              onKeyDown={(e) => {
                if (!canPlay) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openPreview();
                }
              }}
            >
              {video?.urlThumbnail ? (
                <img src={video.urlThumbnail} alt="" />
              ) : (
                <div className="report-detail-thumb__placeholder">
                  <VideoCameraOutlined style={{ color: '#fff', fontSize: 24 }} />
                </div>
              )}
              {canPlay && (
                <div className="report-detail-thumb__play" aria-hidden>
                  <PlayCircleOutlined
                    style={{
                      fontSize: 32,
                      color: '#fff',
                      filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.6))',
                    }}
                  />
                </div>
              )}
            </div>

            <div className="report-detail-video-meta">
              <p className="report-detail-video-title">{video?.title || '-'}</p>
              <p className="report-detail-video-line">
                Competencia: {formatCompetitionLabel(video?.competition)}
              </p>
              <p className="report-detail-video-line">
                Subido por: {formatPersonName(video?.uploadedBy)}
              </p>
              {showUnban ? (
                <div className="report-detail-unban">
                  <p className="report-detail-unban-status">
                    Usuario bloqueado administrativamente
                  </p>
                  <Button
                    icon={<UnlockOutlined />}
                    loading={unbanningUser}
                    disabled={unbanningUser}
                    onClick={() => {
                      if (unbanningUser) return;
                      onUnbanUser?.();
                    }}
                  >
                    Desbanear usuario
                  </Button>
                </div>
              ) : null}
              <p className="report-detail-video-line">
                Fecha de carga:{' '}
                {video?.createdAt
                  ? dayjs(video.createdAt).format('DD/MM/YYYY HH:mm')
                  : '-'}
              </p>
              {isDeleted ? (
                <Tag color="error" style={{ width: 'fit-content', marginTop: 4 }}>
                  Eliminado
                </Tag>
              ) : null}
            </div>
          </div>
        </div>

        <div className="report-detail-split__right">
          <MetaItem label="Reportado por:">
            {formatPersonName(report?.reportedBy)}
          </MetaItem>
          <MetaItem label="Fecha del reporte:">
            {report?.createdAt
              ? dayjs(report.createdAt).format('DD/MM/YYYY HH:mm')
              : '-'}
          </MetaItem>
          <MetaItem label="Motivo del reporte:">
            <Space direction="vertical" size={4}>
              <Tag color="error" style={{ marginInlineEnd: 0 }}>
                {report?.reason?.name || '-'}
              </Tag>
              {report?.reason?.description ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {report.reason.description}
                </Text>
              ) : null}
            </Space>
          </MetaItem>
          <MetaItem label="Tipo de contenido:">
            <span className="report-detail-content-type">
              <VideoCameraOutlined style={{ color: colors.primary.main }} />
              {formatContentType(detail?.contentType)}
            </span>
          </MetaItem>
          <MetaItem label="Estado:">{reportStatusTag(report?.status)}</MetaItem>
        </div>
      </div>

      {canAct && (
        <>
          <Divider style={{ margin: '4px 0 0' }} />
          <div>
            <Title level={5} style={{ marginTop: 8, marginBottom: 16 }}>
              Acciones disponibles
            </Title>
            <Spin spinning={applyingModerationAction}>
              <div className="report-actions-grid">
                {!isDeleted && (
                  <ActionCard
                    icon={<DeleteOutlined />}
                    iconClass="report-action-card__icon--danger"
                    title="Eliminar video"
                    titleClass="report-action-card__title--danger"
                    description="El video será eliminado de la plataforma."
                    disabled={applyingModerationAction}
                    onClick={() => onModerationAction('DELETE_VIDEO')}
                  />
                )}
                <ActionCard
                  icon={<StopOutlined />}
                  iconClass="report-action-card__icon--warning"
                  title="Bloquear usuario"
                  titleClass="report-action-card__title--warning"
                  description="El usuario no podrá ingresar a la aplicación."
                  disabled={applyingModerationAction}
                  onClick={() => onModerationAction('BLOCK_USER')}
                />
                <ActionCard
                  icon={<CloseCircleOutlined />}
                  iconClass="report-action-card__icon--neutral"
                  title="No aplicar acción"
                  titleClass="report-action-card__title--neutral"
                  description="El reporte será descartado y el contenido permanecerá disponible."
                  disabled={applyingModerationAction}
                  onClick={() => onModerationAction('NO_ACTION')}
                />
              </div>
            </Spin>
          </div>
        </>
      )}
    </div>
  );
};

export default ReportDetailContent;
